import {createContext, useContext, useMemo} from 'react'

import {logger} from '#/logger'
import {
  type AvatarColor,
  type Emoji,
} from '#/screens/Onboarding/StepProfile/types'

export type AccountType = 'learner' | 'parent' | 'creator' | 'organisation'

type OnboardingScreen =
  | 'profile'
  | 'goal'
  | 'goal-learner-stage'
  | 'goal-parent-stages'
  | 'goal-parent-topics'
  | 'goal-parent-child-topics'
  | 'goal-creator-role'
  | 'goal-org-type'
  | 'goal-audience'
  | 'goal-topics'
  | 'suggested-accounts'
  | 'suggested-starterpacks'
  | 'find-contacts-intro'
  | 'find-contacts'
  | 'finished'

export type GoalStepResults = {
  accountType: AccountType | null
  displayName: string
  country: string | null
  learningStage: string | null
  gradeLevel: string | null
  familyStages: string[]
  parentTopics: string[]
  childTopics: string[]
  creatorRole: string | null
  orgType: string | null
  orgName: string
  audience: string[]
  topics: string[]
}

const INITIAL_GOAL: GoalStepResults = {
  accountType: null,
  displayName: '',
  country: null,
  learningStage: null,
  gradeLevel: null,
  familyStages: [],
  parentTopics: [],
  childTopics: [],
  creatorRole: null,
  orgType: null,
  orgName: '',
  audience: [],
  topics: [],
}

// Maps an AccountType to the goal sub-screens that should be enabled
const ROLE_SCREENS: Record<AccountType, OnboardingScreen[]> = {
  learner: ['goal-learner-stage', 'goal-topics'],
  parent: [
    'goal-parent-stages',
    'goal-parent-topics',
    'goal-parent-child-topics',
  ],
  creator: ['goal-creator-role', 'goal-audience', 'goal-topics'],
  organisation: ['goal-org-type', 'goal-audience', 'goal-topics'],
}

// Ordered goal screens per role (goal first, then sub-screens)
// Used for the step counter. Avatar is always step 1, goal is step 2.
const ROLE_STEP_ORDERS: Record<AccountType, OnboardingScreen[]> = {
  learner: ['goal', 'goal-learner-stage', 'goal-topics'],
  parent: [
    'goal',
    'goal-parent-stages',
    'goal-parent-topics',
    'goal-parent-child-topics',
  ],
  creator: ['goal', 'goal-creator-role', 'goal-audience', 'goal-topics'],
  organisation: ['goal', 'goal-org-type', 'goal-audience', 'goal-topics'],
}

// Fixed totals per role (Avatar + goal screens). Avatar is always step 1.
const ROLE_TOTAL_STEPS: Record<AccountType, number> = {
  learner: 4, // Avatar → Goal → Stage → Topics
  parent: 5, // Avatar → Goal → FamilyStages → ParentTopics → ChildTopics
  creator: 5, // Avatar → Goal → Role → Audience → Topics
  organisation: 5, // Avatar → Goal → OrgType → Audience → Topics
}

const ALL_GOAL_SUB_SCREENS: OnboardingScreen[] = [
  'goal-learner-stage',
  'goal-parent-stages',
  'goal-parent-topics',
  'goal-parent-child-topics',
  'goal-creator-role',
  'goal-org-type',
  'goal-audience',
  'goal-topics',
]

export type OnboardingState = {
  screens: Record<OnboardingScreen, boolean>
  activeStep: OnboardingScreen
  stepTransitionDirection: 'Forward' | 'Backward'

  goalStepResults: GoalStepResults
  profileStepResults: {
    isCreatedAvatar: boolean
    image?: {
      path: string
      mime: string
      size: number
      width: number
      height: number
    }
    imageUri?: string
    imageMime?: string
    creatorState?: {
      emoji: Emoji
      backgroundColor: AvatarColor
    }
  }
}

export type OnboardingAction =
  | {type: 'next'}
  | {type: 'prev'}
  | {type: 'skip-contacts'}
  | {type: 'finish'}
  | {
      type: 'setGoalAccountType'
      accountType: AccountType
      country: string
      displayName: string
    }
  | {
      type: 'setGoalResults'
      results: Partial<GoalStepResults>
    }
  | {
      type: 'setProfileStepResults'
      isCreatedAvatar: boolean
      image: OnboardingState['profileStepResults']['image'] | undefined
      imageUri: string | undefined
      imageMime: string
      creatorState:
        | {
            emoji: Emoji
            backgroundColor: AvatarColor
          }
        | undefined
    }

export function createInitialOnboardingState(
  {
    starterPacksStepEnabled,
    findContactsStepEnabled,
  }: {
    starterPacksStepEnabled: boolean
    findContactsStepEnabled: boolean
  } = {starterPacksStepEnabled: true, findContactsStepEnabled: false},
): OnboardingState {
  const screens: OnboardingState['screens'] = {
    profile: true,
    goal: true,
    'goal-learner-stage': false,
    'goal-parent-stages': false,
    'goal-parent-topics': false,
    'goal-parent-child-topics': false,
    'goal-creator-role': false,
    'goal-org-type': false,
    'goal-audience': false,
    'goal-topics': false,
    'suggested-accounts': true,
    'suggested-starterpacks': starterPacksStepEnabled,
    'find-contacts-intro': findContactsStepEnabled,
    'find-contacts': findContactsStepEnabled,
    finished: true,
  }

  return {
    screens,
    activeStep: 'profile',
    stepTransitionDirection: 'Forward',
    goalStepResults: INITIAL_GOAL,
    profileStepResults: {
      isCreatedAvatar: false,
      image: undefined,
      imageUri: '',
      imageMime: '',
    },
  }
}

export const Context = createContext<{
  state: OnboardingState
  dispatch: React.Dispatch<OnboardingAction>
} | null>(null)
Context.displayName = 'OnboardingContext'

export function reducer(
  s: OnboardingState,
  a: OnboardingAction,
): OnboardingState {
  let next = {...s}

  const stepOrder = getStepOrder(s)

  switch (a.type) {
    case 'next': {
      const nextIndex = stepOrder.indexOf(next.activeStep) + 1
      const nextStep = stepOrder[nextIndex]
      if (nextStep) {
        next.activeStep = nextStep
      }
      next.stepTransitionDirection = 'Forward'
      break
    }
    case 'prev': {
      const prevIndex = stepOrder.indexOf(next.activeStep) - 1
      const prevStep = stepOrder[prevIndex]
      if (prevStep) {
        next.activeStep = prevStep
      }
      next.stepTransitionDirection = 'Backward'
      break
    }
    case 'skip-contacts': {
      const nextIndex = stepOrder.indexOf('find-contacts') + 1
      const nextStep = stepOrder[nextIndex] ?? 'finished'
      next.activeStep = nextStep
      next.stepTransitionDirection = 'Forward'
      break
    }
    case 'finish': {
      next = createInitialOnboardingState({
        starterPacksStepEnabled: s.screens['suggested-starterpacks'],
        findContactsStepEnabled: s.screens['find-contacts'],
      })
      break
    }
    case 'setGoalAccountType': {
      // Disable all goal sub-screens first, then enable only the role's screens
      const newScreens = {...next.screens}
      for (const s of ALL_GOAL_SUB_SCREENS) {
        newScreens[s] = false
      }
      for (const s of ROLE_SCREENS[a.accountType]) {
        newScreens[s] = true
      }
      next.screens = newScreens
      // Reset sub-step selections from any previous role
      next.goalStepResults = {
        ...INITIAL_GOAL,
        accountType: a.accountType,
        country: a.country,
        displayName: a.displayName,
      }
      break
    }
    case 'setGoalResults': {
      next.goalStepResults = {
        ...next.goalStepResults,
        ...a.results,
      }
      break
    }
    case 'setProfileStepResults': {
      next.profileStepResults = {
        isCreatedAvatar: a.isCreatedAvatar,
        image: a.image,
        imageUri: a.imageUri,
        imageMime: a.imageMime,
        creatorState: a.creatorState,
      }
      break
    }
  }

  const state = {
    ...next,
    hasPrev: next.activeStep !== 'profile',
  }

  logger.debug(`onboarding`, {
    hasPrev: state.hasPrev,
    activeStep: state.activeStep,
    goalStepResults: state.goalStepResults,
    profileStepResults: state.profileStepResults,
  })

  if (s.activeStep !== state.activeStep) {
    logger.debug(`onboarding: step changed`, {activeStep: state.activeStep})
  }

  return state
}

function getStepOrder(s: OnboardingState): OnboardingScreen[] {
  const all: OnboardingScreen[] = [
    'profile',
    'goal',
    'goal-learner-stage',
    'goal-parent-stages',
    'goal-parent-topics',
    'goal-parent-child-topics',
    'goal-creator-role',
    'goal-org-type',
    'goal-audience',
    'goal-topics',
    'suggested-accounts',
    'suggested-starterpacks',
    'find-contacts-intro',
    'find-contacts',
    'finished',
  ]
  return all.filter(screen => s.screens[screen])
}

/**
 * Note: not to be confused with `useOnboardingState`, which just determines if onboarding is active.
 * This hook is for internal state of the onboarding flow (i.e. active step etc).
 *
 * This adds additional derived state to the onboarding context reducer.
 */
export function useOnboardingInternalState() {
  const ctx = useContext(Context)

  if (!ctx) {
    throw new Error(
      'useOnboardingInternalState must be used within OnboardingContext',
    )
  }

  const {state, dispatch} = ctx

  return {
    state: useMemo(() => {
      const HIDDEN_FROM_DISPLAY: OnboardingScreen[] = [
        'goal-learner-stage',
        'goal-parent-stages',
        'goal-parent-topics',
        'goal-parent-child-topics',
        'goal-creator-role',
        'goal-org-type',
        'goal-audience',
        'goal-topics',
        'find-contacts',
        'finished',
      ]
      const DISPLAY_ALIAS: Partial<Record<OnboardingScreen, OnboardingScreen>> =
        {
          'find-contacts': 'find-contacts-intro',
        }
      const stepOrder = getStepOrder(state).filter(
        x => !HIDDEN_FROM_DISPLAY.includes(x),
      )
      const canGoBack = state.activeStep !== stepOrder[0]

      // For goal screens (including sub-steps), use role-specific step ordering
      const GOAL_SUB_SCREENS: OnboardingScreen[] = [
        'goal',
        'goal-learner-stage',
        'goal-parent-stages',
        'goal-parent-topics',
        'goal-parent-child-topics',
        'goal-creator-role',
        'goal-org-type',
        'goal-audience',
        'goal-topics',
      ]

      const role = state.goalStepResults.accountType
      let activeStepIndex: number
      let totalSteps: number

      if (role && GOAL_SUB_SCREENS.includes(state.activeStep)) {
        const roleOrder = ROLE_STEP_ORDERS[role]
        const subIdx = roleOrder.indexOf(state.activeStep)
        // stepIndex 0 (goal) → displayed as step 2 (Avatar is step 1)
        activeStepIndex = subIdx + 1
        totalSteps = ROLE_TOTAL_STEPS[role]
      } else {
        const aliased = DISPLAY_ALIAS[state.activeStep] ?? state.activeStep
        activeStepIndex = stepOrder.indexOf(aliased)
        totalSteps = stepOrder.length
      }

      return {
        ...state,
        canGoBack,
        activeStepIndex,
        totalSteps,
      }
    }, [state]),
    dispatch,
  }
}
